import { assignTyped } from '../utilities/type-converter';

interface Group {
    identifier: string;
    label: string;
    controls: Control[];
    element?: HTMLElement;
}

interface Control {
    identifier: string;
    label: string;
    trigger?: string;
    element?: HTMLElement;
    fieldElement?: HTMLInputElement;
    propertyName?: string;
    styleEl?: HTMLStyleElement;
}

interface AccessibilityConfig {
    unavailableSettings: string;
}

interface AccessibilitySettings {
    fontSize: number;
    moreContrast: boolean;
    brightness: number;
    filterBluelight: number;
    grayscale: boolean;
    darkmode: boolean;
}

const groups: Group[] = [
    {
        identifier: 'thene',
        label: 'Theme',
        controls: [
            {
                identifier: 'darkmode',
                label: 'Darkmode',
                trigger: 'switch',
                propertyName: 'darkmode'
            }
        ]
    }, {
        identifier: 'typeface',
        label: 'Typeface',
        controls: [
            {
                identifier: 'fontSize',
                label: 'Font size',
                trigger: 'custom'
            }
        ]
    }, {
        identifier: 'colorFilter',
        label: 'Color filter',
        controls: [
            {
                identifier: 'grayscale',
                label: 'Grayscale',
                trigger: 'switch',
                propertyName: 'grayscale'
            }, {
                identifier: 'moreContrast',
                label: 'More contrast',
                trigger: 'switch',
                propertyName: 'moreContrast'
            },{
                identifier: 'blueLightFilter',
                label: 'Filter blue light',
                trigger: 'custom'
            }, {
                identifier: 'brightness',
                label: 'Brightness',
                trigger: 'custom'
            }
        ]
    }
];

const defaultOptions: AccessibilityConfig = {
    unavailableSettings: ''
};

const defaultSettings: AccessibilitySettings = {
    fontSize: 0,
    moreContrast: false,
    brightness: 1,
    filterBluelight: -1,
    grayscale: false,
    darkmode: false
};


class AccessibilityElement extends HTMLElement {
    protected options: AccessibilityConfig = defaultOptions;
    protected settings: AccessibilitySettings;

    protected controls: Control[] = [];

    constructor() {
        super();
        if(this.dataset.config) {
            this.options = assignTyped(defaultOptions, JSON.parse(this.dataset.config))
        }
        this.settings = Object.assign({}, defaultSettings);
        try {
            let storedSettings = window.localStorage.getItem('accessibility-settings');
            if(storedSettings && storedSettings != '') {
                this.settings = Object.assign(this.settings, JSON.parse(storedSettings));
            }
            if(this.settings.darkmode) {
                this.processControl('darkmode');
            }
        } catch (ex) {

        }

        window.addEventListener('load', () => {
            this.renderLayout();
        });
    }

    private renderLayout() {
        const lang = window.ThiemeCore.Lang;
        const infoEl: HTMLElement = document.createElement('div');
        infoEl.classList.add('d-flex', 'justify-content-end', 'mb-3');
        infoEl.innerHTML = lang.translate('info', null, 'Here you can adjust various settings for optimal display and better accessibility.', 'accessibility');
        this.appendChild(infoEl);

        groups.forEach((group: Group) => {
            const groupEl: HTMLFieldSetElement = document.createElement('fieldset');
            groupEl.classList.add('form-element');
            const legendEl: HTMLLegendElement = document.createElement('legend');
            legendEl.innerHTML = lang.translate('group.' + group.identifier, null, group.label, 'accessibility');
            groupEl.append(legendEl);

            let hasControls = false;

            const rowEl: HTMLDivElement = document.createElement('div');
            rowEl.classList.add('row');
            groupEl.append(rowEl);

            group.controls.forEach((control: Control) => {
                if(
                    this.options.unavailableSettings !== ''
                    && (',' + this.options.unavailableSettings.toUpperCase() + ',').indexOf(',' + control.identifier.toUpperCase() + ',') > -1) {
                    return;
                }
                const controlEl = this.renderControl(control);
                if(controlEl) {
                    control.element = controlEl;
                    this.controls.push(control);
                    rowEl.appendChild(controlEl);
                    hasControls = true;
                    this.processControl(control.identifier, control, true);
                }
            });

            if(hasControls) {
                group.element = groupEl;
                this.appendChild(groupEl);
            }
        });

        const resetBtnEl: HTMLButtonElement = document.createElement('button');
        resetBtnEl.classList.add('btn', 'btn-secondary');
        resetBtnEl.innerHTML = '<span class="bi bi-x-lg me-2"></span>';
        resetBtnEl.innerHTML += '<span>' + lang.translate('action.resetSettings', null, 'Reset settings', 'accessibility') + '</span>';
        resetBtnEl.addEventListener('click', () => {
            this.settings = Object.assign({}, defaultSettings);
            this.controls.forEach((control: Control) => {
                this.processControl(control.identifier, control, false);
                this.saveSettings();
            });
        });

        const footerEl: HTMLElement = document.createElement('div');
        footerEl.classList.add('d-flex', 'justify-content-center', 'mt-3');
        footerEl.appendChild(resetBtnEl);
        this.appendChild(footerEl);
    }

    private renderControl(control: Control): HTMLElement|null {
        const lang = window.ThiemeCore.Lang;
        let controlEl = document.createElement('div');
        controlEl.classList.add('control', 'form-group');
        control.element = controlEl;
        let label = lang.translate('control.' + control.identifier, null, control.label, 'accessibility');
        switch(control.identifier) {
            case 'fontSize': {
                let stateLabels: {[key: number]: string} = {
                    '0': lang.translate('sizes.0', null, 'Normal', 'accessibility'),
                    '3': lang.translate('sizes.middle', null, 'Middle', 'accessibility'),
                    '6': lang.translate('sizes.max', null, 'Big', 'accessibility')
                };
                const biggerActionLabel = lang.translate('action.bigger', null, 'Font size bigger', 'accessibility');
                const smallerActionLabel = lang.translate('action.bigger', null, 'Font size bigger', 'accessibility');
                controlEl.classList.add('col-12');
                controlEl.innerHTML = `
                    <label class="form-label" for="accessibility_font_size">
                        ${label}
                    </label>
                    <div class="input-group">
                        <button type="button" class="btn btn-outline-secondary" data-action="changeFontSize" data-option="-" title="${smallerActionLabel}">
                            <span class="bi bi-dash"></span>
                        </button>
                        <div class="form-control d-flex align-items-center">
                            <input type="range" min="0" max="6" step="2" name="font-size" class="form-range" id="accessibility_font_size" title="${label}">
                        </div>
                        <button type="button" class="btn btn-outline-secondary" data-action="changeFontSize" data-option="+" title="${biggerActionLabel}">
                            <span class="bi bi-plus"></span>
                        </button>
                    </div>
                    <span class="badge text-bg-secondary range-value"></span>
                `;

                const fieldEl = controlEl.querySelector<HTMLInputElement>('[name="font-size"]')!;
                control.fieldElement = fieldEl;
                const rangeValueBadgeEl = controlEl.querySelector<HTMLInputElement>('.range-value')!;
                const changeFontSizeEls = controlEl.querySelectorAll<HTMLElement>('[data-action="changeFontSize"]');
                const updateVisual = () => {
                    const value = parseInt(fieldEl.value);
                    for(const k in stateLabels) {
                        if(value >= parseInt(k)) {
                            rangeValueBadgeEl.innerHTML = stateLabels[k];
                        }
                    }
                };

                fieldEl.value = this.settings.fontSize.toString();
                fieldEl.addEventListener('change', (ev) => {
                    updateVisual();
                    this.settings.fontSize = parseInt(fieldEl.value);
                    this.processControl(control.identifier, control, true);
                });
                fieldEl.addEventListener('input', (ev) => {
                    updateVisual();
                });

                changeFontSizeEls.forEach((el: HTMLElement) => {
                    el.addEventListener('click', () => {
                        if(el.dataset.option === '+') {
                            if(parseInt(fieldEl.value) < parseInt(fieldEl.getAttribute('max') ?? '')) {
                                fieldEl.value = (parseInt(fieldEl.value) + parseInt(fieldEl.getAttribute('step') ?? '')).toString();
                            }
                        }
                        if(el.dataset.option === '-') {
                            if(parseInt(fieldEl.value) > parseInt(fieldEl.getAttribute('min') ?? '')) {
                                fieldEl.value = (parseInt(fieldEl.value) - parseInt(fieldEl.getAttribute('step') ?? '')).toString();
                            }
                        }
                        fieldEl.dispatchEvent(new Event('change'));
                    });
                });

                updateVisual();
                this.processControl(control.identifier, control, true);
                break;
            }
            case 'brightness': {
                controlEl.classList.add('col-12');
                controlEl.innerHTML = `
                    <label class="form-label" for="accessibility_brightness">
                        ${label}
                    </label>
                    <div class="input-group">
                        <div class="input-group-text">
                            <span class="bi bi-brightness-low"></span>
                        </div>
                        <div class="form-control d-flex align-items-center">
                            <input type="range" min="0.3" max="1" step="0.1" name="brightness" class="form-range" id="accessibility_brightness" title="${label}">
                        </div>
                        <div class="input-group-text">
                            <span class="bi bi-brightness-high-fill"></span>
                        </div>
                    </div>
                `;

				const fieldEl: HTMLInputElement = controlEl.querySelector('[name="brightness"]')!;
                control.fieldElement = fieldEl;
                if(fieldEl) {
                    fieldEl.value = this.settings.brightness.toString();
                    fieldEl.addEventListener('change', (ev) => {
                        this.settings.brightness = parseFloat(fieldEl.value);
                        this.processControl(control.identifier, control, true);
                    });
                    fieldEl.addEventListener('input', (ev) => {
                        this.settings.brightness = parseFloat(fieldEl.value);
                        this.processControl(control.identifier, control, true);
                    });
                    this.processControl(control.identifier, control, true);
                }
                break;
            }
            case 'grayscale': {
                controlEl.classList.add('col-12', 'col-sm-6');
                controlEl.innerHTML = `
                    <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" name="grayscale" id="accessibility_filter_grayscale">
                        <label class="form-check-label" for="accessibility_filter_grayscale">
                            ${label}
                        </label>
                    </div>
                `;

				const fieldEl: HTMLInputElement = controlEl.querySelector('[name="grayscale"]')!;
                control.fieldElement = fieldEl;
                if(this.settings.grayscale) {
                    fieldEl.checked = true;
                }
                fieldEl.addEventListener('change', () => {
                    this.settings.grayscale = fieldEl.checked;
                    this.processControl(control.identifier, control, true);
                });
                this.processControl(control.identifier, control, true);
                break;
            }
            case 'darkmode': {
                controlEl.classList.add('col-12', 'col-sm-6');
                controlEl.innerHTML = `
                    <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" name="darkmode" id="accessibility_darkmode">
                        <label class="form-check-label" for="accessibility_darkmode">
                            ${label}
                        </label>
                    </div>
                `;

				const fieldEl: HTMLInputElement = controlEl.querySelector('[name="darkmode"]')!;
                control.fieldElement = fieldEl;
                if(this.settings.darkmode) {
                    fieldEl.checked = true;
                }
                fieldEl.addEventListener('change', () => {
                    this.settings.darkmode = fieldEl.checked;
                    this.processControl(control.identifier, control, true);
                });
                this.processControl(control.identifier, control, true);
                break;
            }
            case 'moreContrast': {
                controlEl.classList.add('col-12', 'col-sm-6');
                controlEl.innerHTML = `
                    <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" name="more-contrast" id="accessibility_more_contrast">
                        <label class="form-check-label" for="accessibility_more_contrast">
                            ${label}
                        </label>
                    </div>
                `;

				const fieldEl: HTMLInputElement = controlEl.querySelector('[name="more-contrast"]')!;
                control.fieldElement = fieldEl;
                if(this.settings.moreContrast) {
                    fieldEl.checked = true;
                }
                fieldEl.addEventListener('change', () => {
                    this.settings.moreContrast = fieldEl.checked;
                    this.processControl(control.identifier, control, true);
                });
                this.processControl(control.identifier, control, true);
                break;
            }
            case 'blueLightFilter': {
                controlEl.classList.add('col-12');
                controlEl.innerHTML = `
                    <label class="form-label" for="accessibility_filter_bluelight">
                        ${label}
                    </label>
                    <div class="input-group">
                        <div class="input-group-text">
                            <span class="bi bi-circle"></span>
                        </div>
                        <div class="form-control d-flex align-items-center">
                            <input type="range" min="0" max="1" step="0.1" name="filter-bluelight" class="form-range" id="accessibility_filter_bluelight">
                        </div>
                        <div class="input-group-text">
                            <span class="bi bi-circle-fill"></span>
                        </div>
                    </div>
                `;

				const fieldEl: HTMLInputElement = controlEl.querySelector('[name="filter-bluelight"]')!;
                control.fieldElement = fieldEl;
                if(this.settings.filterBluelight > -1) {
                    fieldEl.value = this.settings.filterBluelight.toString();
                }
                fieldEl.addEventListener('change', () => {
                    this.settings.filterBluelight = parseFloat(fieldEl.value);
                    this.processControl(control.identifier, control, true);
                });
                fieldEl.addEventListener('input', () => {
                    this.settings.filterBluelight = parseFloat(fieldEl.value);
                    this.processControl(control.identifier, control, true);
                });
                this.processControl(control.identifier, control, true);
                break;
            }
        }

        return controlEl;
    }


	private processControl(identifier: string, control?: Control, store: boolean = false) {
        let fieldEl: HTMLInputElement|null = (control && control.fieldElement) ? control.fieldElement : null;
        switch(identifier) {
            case 'fontSize':
                const documentStyle = window.getComputedStyle(document.documentElement)
                const intialRootFontSize = documentStyle.getPropertyValue('--bs-root-font-size-initial');
                let newFontSize = intialRootFontSize;
                if(parseInt(intialRootFontSize) > 0) {
                    newFontSize = (parseInt(intialRootFontSize) + this.settings.fontSize).toString() + 'px';
                }
                document.documentElement.style.setProperty('--bs-root-font-size', newFontSize);
                if(fieldEl) {
                    fieldEl.value = this.settings.fontSize.toString();
                }
                break;
            case 'moreContrast':
                if(fieldEl) {
                    fieldEl.checked = this.settings.moreContrast;
                    if(this.settings.moreContrast) {
                        control!.styleEl = document.createElement('style');
                        control!.styleEl.innerHTML = ':root { --bs-primary: #000000; --bs-secondary: #222222; }';
                        document.querySelector('html > head')!.append(control!.styleEl);
                        document.querySelector('html')!.classList.add('accessibility-more-contrast');
                    } else {
                        if(control!.styleEl) {
                            control!.styleEl.remove();
                            delete(control!.styleEl);
                        }
                        document.querySelector('html')!.classList.remove('accessibility-more-contrast');
                    }
                }
                break;
            case 'brightness':
                const brightnessValue = this.settings.brightness;
                let brightnessOverlayEl: HTMLElement|null = document.querySelector('#accessibility_brightness_overlay');
                if(brightnessValue < 1 && brightnessOverlayEl === null) {
                    brightnessOverlayEl = document.createElement('div');
                    brightnessOverlayEl.setAttribute('id', 'accessibility_brightness_overlay');
                    Object.assign(brightnessOverlayEl.style, {
                        display: 'none',
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                        position: 'fixed',
                        zIndex: 92890890299,
                        pointerEvents: 'none',
                        inset: '-10px',
                        width: 'auto',
                        height: 'auto',
                        mixBlendMode: 'multiply',
                        margin: 0,
                        padding: 0
                    });
                    document.querySelector('body')!.appendChild(brightnessOverlayEl);
                }
                if(brightnessOverlayEl) {
                    if(brightnessValue < 1) {
                        brightnessOverlayEl.style.display = 'block';
                        brightnessOverlayEl.style.backgroundColor = 'rgba(0, 0, 0, '+(1-brightnessValue)+')';
                    } else {
                        brightnessOverlayEl.style.display = 'none';
                    }
                }
                if(fieldEl) {
                    fieldEl.value = this.settings.brightness.toString();
                }
                break;
            case 'grayscale':
                if(fieldEl) {
                    fieldEl.checked = this.settings.grayscale;
                    if(this.settings.grayscale) {
                        document.querySelector('html')!.classList.add('accessibility-grayscale');
                    } else {
                        document.querySelector('html')!.classList.remove('accessibility-grayscale');
                    }
                }
                break;
            case 'darkmode':
                if(fieldEl) {
                    fieldEl.checked = this.settings.darkmode;
                }
                if(this.settings.darkmode) {
                    document.querySelector('html')?.setAttribute('data-bs-theme', 'dark');
                } else {
                    document.querySelector('html')?.removeAttribute('data-bs-theme');
                }
                break;
            case 'blueLightFilter':
                const filterBluelightValue = this.settings.filterBluelight;
                let filterBluelighOverlayEl: HTMLElement|null = document.querySelector('#accessibility_bluelight_filter_overlay');
                if(filterBluelightValue && filterBluelighOverlayEl === null) {
                    filterBluelighOverlayEl = document.createElement('div');
                    filterBluelighOverlayEl.setAttribute('id', 'accessibility_bluelight_filter_overlay');
                    Object.assign(filterBluelighOverlayEl.style, {
                        display: 'none',
                        backgroundColor: 'rgba(255, 147, 41, 0)',
                        position: 'fixed',
                        zIndex: 92890890299,
                        pointerEvents: 'none',
                        inset: '-10px',
                        width: 'auto',
                        height: 'auto',
                        mixBlendMode: 'multiply',
                        margin: 0,
                        padding: 0
                    });
                    document.querySelector('body')!.appendChild(filterBluelighOverlayEl);
                }

                if(filterBluelighOverlayEl) {
                    if(filterBluelightValue > 0) {
                        filterBluelighOverlayEl.style.display = 'block';
                        filterBluelighOverlayEl.style.backgroundColor = 'rgba(255, 147, 41, '+filterBluelightValue+')';
                    } else {
                        filterBluelighOverlayEl.style.display = 'none';
                    }
                }
                if(fieldEl) {
                    fieldEl.value = this.settings.filterBluelight.toString();
                }
                break;
        }
		if(store) {
			this.saveSettings();
		}
	}

	private saveSettings() {
		window.localStorage.setItem('accessibility-settings', JSON.stringify(this.settings));
	}
}


customElements.define("th-accessibility", AccessibilityElement);

export type { AccessibilityConfig };
export default AccessibilityElement;
